<template>
  <footer>
    <div class="footer-26">
      <div class="container">
        <div class="mb-10 lg:mb-14">
          <NuxtLink class="logo" :to="localePath('/')">
            <NuxtImg loading="lazy" v-no-right-click :src="companyInfo.logo" alt="logo" />
          </NuxtLink>
        </div>
        <div class="columns">
          <div class="col">
            <div v-if="!companyInfo.hideHelpTitle" class="title">
              {{ t("supplier.footer.getHelp") }}
            </div>
            <template v-if="$te('supplier.footer.chatWithUs')">
              <a
                v-if="companyInfo.whatsapp"
                class="link-item"
                :href="`https://wa.me/${companyInfo.whatsapp}`"
                target="_blank"
                rel="noopener"
              >
                <icon-chat />
                {{ t("supplier.footer.chatWithUs") }}
              </a>
              <NuxtLink v-else class="link-item" :to="localePath('/')">
                <icon-chat />
                {{ t("supplier.footer.chatWithUs") }}
              </NuxtLink>
            </template>
            <template v-if="companyInfo.phones?.length">
              <a
                v-for="(phoneItem, index) in companyInfo.phones"
                :key="index"
                class="link-item"
                :href="`tel:${phoneItem.number}`"
              >
                <icon-call />
                <span dir="ltr">{{ phoneItem.number }}</span>
              </a>
            </template>
            <a
              v-else-if="companyInfo.phoneNumber"
              class="link-item"
              :href="`tel:${companyInfo.phoneNumber}`"
            >
              <icon-call />
              <span dir="ltr">+{{ companyInfo.phoneNumber }}</span>
            </a>
            <a
              v-if="companyInfo.email"
              class="link-item"
              :href="`mailto:${companyInfo.email}`"
            >
              <icon-email-closed />
              {{ companyInfo.email }}
            </a>
            <TheFooterSocialMedias
              v-if="Object.keys(socialMedias).length"
              class="mt-2.5"
              :type="2"
              :socials="socialMedias"
            />
          </div>
          <div class="col">
            <div class="title">
              {{ companyInfo.name }}
            </div>
            <NuxtLink
              v-for="(item, index) in footerItems"
              :key="index"
              :to="item.itsOuterLink ? item.target : localePath(item.target)"
              :target="item.itsOuterLink ? '_blank' : undefined"
              class="link-item"
            >
              {{ t(item?.title) }}
            </NuxtLink>
          </div>
          <div v-if="popularCities?.length" class="col">
            <div class="title">
              {{ t("supplier.footer.citiesTitle") }}
            </div>
            <NuxtLink
              v-for="city in popularCities.slice(0, 8)"
              :key="city.city.id"
              :to="localePath(`/things-to-do-in-${city?.city.slug}`)"
              class="link-item"
            >
              {{ city.city.name }}
            </NuxtLink>
          </div>
        </div>
      </div>
    </div>
    <div class="bg-gray-5 py-7">
      <div
        class="container flex flex-col md:flex-row md:items-center md:justify-between gap-4"
      >
        <div
          v-if="companyInfo.acceptedPayments?.length"
          class="flex gap-[3px]"
        >
          <NuxtImg
            loading="lazy"
            v-for="acceptedPayment in companyInfo.acceptedPayments"
            :key="acceptedPayment"
            v-no-right-click
            :src="`/${acceptedPayment}.svg`"
            :alt="`${acceptedPayment} payment`"
            class="h-5 w-8"
          />
        </div>
        <div
          v-if="$te('supplier.footer.endCaption')"
          class="text-gray-1 flex items-center"
        >
          <icon-c class="text-[16px] me-2.5 !mb-0" />
          <span class="text-mobile-caption-0 title-font">{{
            t("supplier.footer.endCaption")
          }}</span>
        </div>
      </div>
    </div>
  </footer>
</template>

<script lang="ts" setup>
import useCompanyPhone from "../../../composables/useCompanyPhone";
import useCompanyEmail from "../../../composables/useCompanyEmail";
import useWhatsappNumber from "../../../composables/useWhatsappNumber";
import { useAsyncData, useRuntimeConfig } from "nuxt/app";
import TheFooterSocialMedias from "./TheFooterSocialMedias.vue";
import type { ITopCities } from "../../../types/general";
import useFooterSocialMedias from "../../../composables/useFooterSocialMedias";

const { t, locale } = useI18n();
const localePath = useLocalePath();

const config = useRuntimeConfig() as unknown as RuntimeConfiguration;

const socialMedias = await useFooterSocialMedias(
  config.public.footer.socialLinks,
);

const whatsappNumber = useWhatsappNumber();
const companyEmail = useCompanyEmail();
const companyPhone = useCompanyPhone();
const companyInfo = {
  name: config.public.name,
  logo: config.public.footer.logo,
  get phoneNumber() {
    return companyPhone.value;
  },
  get whatsapp() {
    return whatsappNumber.value;
  },
  hideHelpTitle: config.public.footer?.hideHelpTitle ?? false,
  phones: config.public.footer.phones,
  acceptedPayments: config.public.footer?.acceptedPayments ?? [],
  get email() {
    return companyEmail.value;
  },
};

const footerItems = config.public.footer.items ?? [];

const { data: popularCities } = await useAsyncData<ITopCities[] | null>(
  `footer-top-cities-${locale.value}`,
  () => customFetch(`api/top-cities?lang=${locale.value}`),
);
</script>

<style scoped>
.footer-26 {
  @apply bg-footer-background pt-16 lg:!pt-24 pb-12 lg:!pb-20 cursor-context-menu relative;
}
.footer-26 .logo {
  @apply flex h-12;
}
.footer-26 .logo img {
  @apply h-full w-auto object-contain;
}
.footer-26 .columns {
  @apply flex flex-col lg:flex-row lg:justify-between gap-10;
}
.footer-26 .col {
  @apply flex flex-col gap-[30px];
}
.footer-26 .title {
  @apply text-mobile-heading-5 md:text-heading-5 text-gray-2;
}
.footer-26 .link-item {
  @apply text-mobile-heading-5 md:text-heading-5 text-footer-text-color flex items-center gap-2;
}
.footer-26 .link-item svg {
  @apply text-[16px] !mb-0;
}
</style>
